import Link from '@/routing/AppLink';
import { useTranslation } from 'next-i18next/pages';

import { useNavMenuDisclosure } from '@/hooks/apps/useNavMenuDisclosure';

import { NAV_MENU_ITEMS } from '@/data/shared/navMenu';

import { resolveNavLinkAttributes } from '@/utils/resolveNavLinkAttributes';

import { ExternalLinkMark } from '../shared/ExternalLinkMark';
import { NavMenuAnchor } from '../shared/NavMenuAnchor';
import type { HeaderVariantType } from './Header';
import * as s from './Header.styles';

/**
 * ### HeaderNavProps
 *
 * Appearance and route context the desktop navigation needs from the header.
 */
export interface HeaderNavProps {
  /**
   * ### variant
   *
   * Header colour scheme; decides the resting colour of primary links.
   */
  variant: HeaderVariantType;

  /**
   * ### isActivePath
   *
   * Reports whether a navigation URL matches the current route, for the active style.
   */
  isActivePath: (pathname: string) => boolean;
}

/**
 * ### HeaderNav
 *
 * The desktop primary navigation with hover and keyboard operated submenus.
 * A primary link with a submenu exposes `aria-expanded` and, while open,
 * `aria-controls`; submenus are lists of links, not ARIA menus, so no menu
 * roles are claimed.
 * Mount it with a route-dependent `key` so a submenu opened by focus closes on navigation.
 *
 * @param props Colour variant and active-route predicate
 * @returns The `<nav>` element with every primary item
 * @see {@link useNavMenuDisclosure} Open-state rules for pointer, focus, and Escape
 */
export function HeaderNav({ variant, isActivePath }: HeaderNavProps) {
  const { t } = useTranslation('common');
  const {
    openIndex,
    closeAll,
    getGroupHandlers,
    getSubmenuMouseOver,
    getTriggerRef,
  } = useNavMenuDisclosure();
  const menuItems = NAV_MENU_ITEMS;

  return (
    <nav
      className={s.navContainer}
      onMouseLeave={closeAll}
      aria-label={t('주 메뉴')}
    >
      <ul className={s.navList}>
        {menuItems.map((menu, index) => {
          const submenu = menu.depth2;
          const isOpen = submenu !== undefined && openIndex === index;
          const submenuId = `pc-nav-depth2-${index}`;
          const { isExternal, target, rel } = resolveNavLinkAttributes(
            menu.depth1,
          );

          return (
            <li
              key={menu.depth1.url}
              className={s.depth1Container}
              {...getGroupHandlers(index)}
            >
              <Link
                ref={getTriggerRef(index)}
                href={submenu ? submenu[0].url : menu.depth1.url}
                className={s.depth1Item(isActivePath(menu.depth1.url), variant)}
                target={target}
                rel={rel}
                prefetch={false}
                aria-expanded={submenu ? isOpen : undefined}
                aria-controls={submenu ? submenuId : undefined}
              >
                {t(menu.depth1.label)}
                {isExternal && <ExternalLinkMark size={20} />}
              </Link>
              {isOpen && (
                <ul
                  id={submenuId}
                  className={s.depth2Container}
                  onMouseOver={getSubmenuMouseOver(index)}
                >
                  {submenu.map((subMenu) => (
                    <li key={subMenu.url}>
                      <NavMenuAnchor
                        link={subMenu}
                        className={`h-full w-full ${s.depth2Item(
                          isActivePath(subMenu.url),
                        )}`}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
